import type { CustomerStore } from '@stores/me/customer/interfaces'
import type { Customer } from '@brighterly/lib-core-types/customer/interfaces'
import type { DisciplineInKid } from '@brighterly/lib-core-types/discipline/interfaces'
import type { KidInCustomer } from '@brighterly/lib-core-types/kid/interfaces'

import { PaymentProvider } from '@brighterly/lib-core-types/subscription/enums'
import { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import { Status } from '@brighterly/lib-core-types/customer/enums'

import { useDisciplineStore } from '@stores/discipline'
import { useKidStore } from '@stores/kid'

import { listDemoNoShowStatuses } from '@config/booking'
import { isFullArray } from '@helpers/array'
import { isCompletedBooking, isDemoNoShowBooking } from '@utils/booking'
import { experiments } from '@utils/me/customer/experiments'
import { marketingData } from '@utils/me/customer/marketing-data'

const min = 60_000
const hour = min * 60
const TTL_MS = hour * 24

export const isStale = (state: CustomerStore): boolean => !state.updatedAt || (Date.now() - state.updatedAt) > TTL_MS

const getFirstName = (state: CustomerStore): string => state.customer?.first_name || ''
const getLastName = (state: CustomerStore): string => state.customer?.last_name || ''
const getFullName = (state: CustomerStore): string => {
  return `${getFirstName(state)} ${getLastName(state)}`
}

const getKidInCustomer = (state: CustomerStore): KidInCustomer | null => {
  const kidStore = useKidStore()
  return state?.customer?.kids.find(kid => kid.id === kidStore.activeKidId) || null
}

const getActiveDisciplineInKid = (state: CustomerStore): DisciplineInKid | null => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return null
  } else {
    const disciplineStore = useDisciplineStore()
    const id = disciplineStore.activeDiscipline?.id || null
    if (!id) {
      return null
    } else {
      const discipline = kid.disciplines.find(discipline => discipline.id === id)
      return discipline || null
    }
  }
}

const isActiveDisciplinePaid = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    const discipline = getActiveDisciplineInKid(state)
    return !!discipline?.is_paid
  }
}

const isReversedDisciplinePaid = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    const discipline = getActiveKidReversedDisciplineInfo(state)
    return !!discipline?.is_paid
  }
}

const isActiveDisciplineDemoCompleted = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    const discipline = getActiveDisciplineInKid(state)
    return isCompletedBooking(discipline?.demo || null)
  }
}

const isSomeDisciplineDemoCompleted = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    const disciplines = kid?.disciplines || []
    if (!isFullArray(disciplines)) {
      return false
    } else {
      return disciplines.some(discipline => isCompletedBooking(discipline?.demo || null))
    }
  }
}

const isActiveDisciplineDemoNoShow = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    const discipline = getActiveDisciplineInKid(state)
    const status = discipline?.demo?.status || null
    return status ? listDemoNoShowStatuses.includes(status) : false
  }
}

const isAllDisciplinesDemoCompleted = (state: CustomerStore): boolean => {
  const kid = getKidInCustomer(state)
  if (!kid) {
    return false
  } else {
    if (kid.disciplines.length <= 1) {
      return false
    } else {
      const completedDemos = kid.disciplines.map(discipline => isCompletedBooking(discipline?.demo || null))
      return completedDemos.every(completedDemo => completedDemo)
    }
  }
}

const getActiveKidDisciplines = (state: CustomerStore): DisciplineInKid[] => {
  return getKidInCustomer(state)?.disciplines || []
}

const getActiveKidDisciplinesWithoutCurrent = (state: CustomerStore): DisciplineInKid[] => {
  const disciplines = getActiveKidDisciplines(state)
  const activeDiscipline = getActiveDisciplineInKid(state)

  return disciplines.filter(discipline => discipline.code !== activeDiscipline?.code)
}

const isActiveKidDisciplinesWithoutCurrent = (state: CustomerStore): boolean => isFullArray(getActiveKidDisciplinesWithoutCurrent(state))

const getActiveKidReversedDisciplineInfo = (state: CustomerStore): DisciplineInKid | null => {
  if (!isActiveKidDisciplinesWithoutCurrent(state)) {
    return null
  } else {
    const disciplines = getActiveKidDisciplinesWithoutCurrent(state)
    return disciplines?.[0] || null
  }
}

export const getters = {
  hasData: (state: CustomerStore): boolean => !!state.customer,
  isStale: (state: CustomerStore): boolean => isStale(state),
  isLoading: (state: CustomerStore): boolean => state.status === 'loading',
  isIdle: (state: CustomerStore): boolean => state.status === 'idle',
  isReady: (state: CustomerStore): boolean => state.status === 'ready',
  isError: (state: CustomerStore): boolean => state.status === 'error',
  isDeal: (state: CustomerStore): boolean => state.customer?.status === Status.Deal,
  isLead: (state: CustomerStore): boolean => state.customer?.status === Status.Lead,
  isAiSummary:  (state: CustomerStore): boolean => state.customer?.ai_summarize || false,
  hasAiSummaries:  (state: CustomerStore): boolean => state.customer?.has_ai_summaries || false,
  isTypeDemo: (state: CustomerStore): boolean => state.customer?.type === 'demo',
  isTypePaid: (state: CustomerStore): boolean => state.customer?.type === 'paid',
  getContactData: (state: CustomerStore): Customer['contact_data'] | null => state.customer?.contact_data || null,
  getFirstName: (state: CustomerStore): string => getFirstName(state),
  getLastName: (state: CustomerStore): string => getLastName(state),
  getFullName: (state: CustomerStore): string => getFullName(state),
  getEmail: (state: CustomerStore): string => state.customer?.email || '',
  getPhone: (state: CustomerStore): string => state.customer?.phone || '',
  getCustomer: (state: CustomerStore): CustomerStore['customer'] => state.customer,
  getCustomerId: (state: CustomerStore): number | null => state.customer?.id || null,
  isScreenRecording: (state: CustomerStore): boolean => state.customer?.screen_recording || false,
  getKids: (state: CustomerStore): Customer['kids'] => state.customer?.kids || [],
  getKidInCustomer: (state: CustomerStore): KidInCustomer | null => getKidInCustomer(state),
  getActiveDisciplineInKid: (state: CustomerStore): DisciplineInKid | null => getActiveDisciplineInKid(state),
  isActiveDisciplineDemoInKid: (state: CustomerStore): boolean => !!getActiveDisciplineInKid(state)?.demo?.id,
  getActiveKidDisciplines: (state: CustomerStore): DisciplineInKid[] => getActiveKidDisciplines(state),
  getActiveKidDisciplinesWithoutCurrent: (state: CustomerStore): DisciplineInKid[] => getActiveKidDisciplinesWithoutCurrent(state),
  isActiveKidDisciplinesWithoutCurrent: (state: CustomerStore): boolean => isActiveKidDisciplinesWithoutCurrent(state),
  getActiveKidReversedDisciplineInfo: (state: CustomerStore): DisciplineInKid | null => getActiveKidReversedDisciplineInfo(state),
  isActiveKidReversedDisciplineInfo: (state: CustomerStore): boolean => !!getActiveKidReversedDisciplineInfo(state),
  getKidNameInCustomer: (state: CustomerStore): KidInCustomer['name'] => getKidInCustomer(state)?.name || 'kid',
  isAbleToBookMoreDisciplines: (state: CustomerStore): boolean => {
    const disciplineStore = useDisciplineStore()
    const disciplines = getKidInCustomer(state)?.disciplines || []
    const allDemosCompleted = (disciplines.length > 1) && disciplines.every(discipline => isCompletedBooking(discipline?.demo || null))
    if (allDemosCompleted) {
      return false
    } else {
      const activeDisciplineId = disciplineStore.activeDiscipline?.id || null
      if (!activeDisciplineId) {
        return false
      } else {
        const currentDiscipline = disciplines.find(discipline => discipline.id === activeDisciplineId) || null
        if (!currentDiscipline) {
          return false
        } else {
          const isEla = (currentDiscipline.code === Discipline.ELA)
          const isMath = (currentDiscipline.code === Discipline.Math)

          if (isEla) {
            const mathDiscipline = disciplines.find(discipline => discipline.code === Discipline.Math) || null
            if (!mathDiscipline) {
              return true
            } else {
              return isDemoNoShowBooking(mathDiscipline.demo)
            }
          } else if (isMath) {
            const elaDiscipline = disciplines.find(discipline => discipline.code === Discipline.ELA) || null
            if (!elaDiscipline) {
              return true
            } else {
              return isDemoNoShowBooking(elaDiscipline.demo)
            }
          } else {
            return false
          }
        }
      }
    }
  },

  isActiveDisciplinePaid: (state: CustomerStore): boolean => isActiveDisciplinePaid(state),
  isDisciplinePaidById: (state: CustomerStore) => (id: number): boolean => {
    const disciplines = state.customer?.kids?.flatMap(kid => kid.disciplines) ?? []
    const discipline = disciplines.find(item => item.id === id)
    return !!discipline?.is_paid
  },
  isReversedDisciplinePaid: (state: CustomerStore): boolean => isReversedDisciplinePaid(state),
  isActiveDisciplineDemoCompleted: (state: CustomerStore): boolean => isActiveDisciplineDemoCompleted(state),
  isSomeDisciplineDemoCompleted: (state: CustomerStore): boolean => isSomeDisciplineDemoCompleted(state),
  isActiveDisciplineDemoNoShow: (state: CustomerStore): boolean => isActiveDisciplineDemoNoShow(state),
  isAllDisciplinesDemoCompleted: (state: CustomerStore): boolean => isAllDisciplinesDemoCompleted(state),
  isBookDemoAllowed: (state: CustomerStore): boolean => state.customer?.is_book_demo_allowed || false,
  isClarityReady: (state: CustomerStore): CustomerStore['clarityReady'] => state.clarityReady,
  getPaymentProvider: (state: CustomerStore): PaymentProvider | null => state.customer?.payment_provider ?? null,
  isPaymentProviderStripe: (state: CustomerStore): boolean => state.customer?.payment_provider === PaymentProvider.Stripe,
  isPaymentProviderSolid: (state: CustomerStore): boolean => state.customer?.payment_provider === PaymentProvider.Solidgate,
  ...experiments,
  ...marketingData,
}
